import {
  FORM_CONTAINER_COMPONENT_TYPE,
  formComponentTypeFromResourceType,
  isFormContainerResourceType,
} from 'src/components/form/form-resource-types';
import { ValidationOutcomes, ValidationResult } from 'src/validation';
import { debugError } from '..';
import {
  cleanHeadlessData,
  getColumnClassInfo,
  isObjectEmpty,
  stripPath,
  validateComponentProps,
} from './utils';

const getResourceType = (componentData: any): string => {
  const resourceType =
    componentData?.[':type'] ||
    componentData?.['sling:resourceType'] ||
    componentData?.resourceType;

  return typeof resourceType === 'string' ? resourceType : '';
};

const getRenderableProps = (componentData: any): any => {
  if (
    componentData &&
    Object.hasOwn(componentData, 'vaaData') &&
    !isObjectEmpty(componentData.vaaData)
  ) {
    return componentData.vaaData;
  }

  return componentData;
};

const processFormComponent = (
  componentName: string,
  componentData: any,
  columnClassNames: string,
  flatComponentsArray: any[],
) => {
  const resourceType = getResourceType(componentData);
  const componentType = formComponentTypeFromResourceType(resourceType);

  if (componentType !== FORM_CONTAINER_COMPONENT_TYPE) {
    return;
  }

  const componentProps = getRenderableProps(componentData);
  const formComponentData: any = {};

  formComponentData.props = componentProps;
  formComponentData.id = componentData.id || componentProps.id || componentName;
  formComponentData.name = componentName;
  formComponentData.columnClassNames = columnClassNames;

  validateComponent(formComponentData, componentType, flatComponentsArray);
};

const processExperienceFragmentData = async (
  experienceFragmentData: any,
  flatComponentsArray: any[],
  columnClassNames = 'aem-GridColumn aem-GridColumn--default--12',
) => {
  if (
    !Object.hasOwn(experienceFragmentData, 'vaaData') ||
    !Object.hasOwn(experienceFragmentData.vaaData, 'content')
  ) {
    return;
  }

  const { content } = experienceFragmentData.vaaData;

  for (const experienceFragmentItemKey of Object.keys(content)) {
    const experienceFragmentItem = content[experienceFragmentItemKey];
    const experienceFragmentItemResourceType = getResourceType(
      experienceFragmentItem,
    );
    const experienceFragmentItemType =
      stripPath(experienceFragmentItemResourceType) || 'unknown';

    if (isFormContainerResourceType(experienceFragmentItemResourceType)) {
      processFormComponent(
        experienceFragmentItemKey,
        experienceFragmentItem,
        columnClassNames,
        flatComponentsArray,
      );
    } else if (experienceFragmentItemType === 'container') {
      await processContainerData(
        experienceFragmentItemKey,
        experienceFragmentItem,
        flatComponentsArray,
      );
    } else if (experienceFragmentItemType !== 'unknown') {
      const componentData: any = {};
      componentData.props = experienceFragmentItem;
      componentData.id = experienceFragmentItemKey;
      componentData.name = experienceFragmentItemKey;
      componentData.columnClassNames = columnClassNames;
      validateComponent(
        componentData,
        experienceFragmentItemType,
        flatComponentsArray,
      );
    }
  }
};

// Extracts the items(components) from the AEM container and adds to the array of components (flatComponentsArray)
async function processContainerData(
  _containerName: string,
  containerData: any,
  flatComponentsArray: any[],
) {
  const rawContainerItems = containerData?.[':items'];
  const containerItems =
    rawContainerItems &&
    typeof rawContainerItems === 'object' &&
    !Array.isArray(rawContainerItems)
      ? rawContainerItems
      : {};
  const authoredOrder = containerData?.[':itemsOrder'];
  const containerItemsOrder =
    Array.isArray(authoredOrder) && authoredOrder.length > 0
      ? authoredOrder
      : Object.keys(containerItems);
  const COMPONENTS_WITHOUT_VAA_DATA = ['slideshow-banner'];

  for (const containerItemName of containerItemsOrder) {
    const containerItem = containerItems[containerItemName];
    if (
      !containerItem ||
      typeof containerItem !== 'object' ||
      Array.isArray(containerItem)
    ) {
      continue;
    }
    const containerItemResourceType = getResourceType(containerItem);
    const containerItemType = stripPath(containerItemResourceType) || 'unknown';
    const containerItemColumnClassNames =
      containerData.columnClassNames?.[containerItemName] ||
      'aem-GridColumn aem-GridColumn--default--12';

    if (isFormContainerResourceType(containerItemResourceType)) {
      processFormComponent(
        containerItemName,
        containerItem,
        containerItemColumnClassNames,
        flatComponentsArray,
      );
    } else if (containerItemType === 'container') {
      await processContainerData(
        containerItemName,
        containerItem,
        flatComponentsArray,
      );
    } else if (containerItemType === 'experience-fragment') {
      await processExperienceFragmentData(
        containerItem,
        flatComponentsArray,
        containerItemColumnClassNames,
      );
    } else {
      // Item is a (storybook) component
      // check for supported types from the lookup file
      if (
        Object.hasOwn(containerItem, 'vaaData') &&
        !isObjectEmpty(containerItem.vaaData)
      ) {
        const containerItemData: any = {};
        containerItemData.props = containerItem.vaaData;
        containerItemData.id = containerItem.id;
        containerItemData.name = containerItemName;
        containerItemData.columnClassNames = containerItemColumnClassNames;

        validateComponent(
          containerItemData,
          containerItemType,
          flatComponentsArray,
        );
      } else if (
        COMPONENTS_WITHOUT_VAA_DATA.includes(containerItemType) &&
        !isObjectEmpty(containerItem)
      ) {
        const containerItemData: any = {};
        containerItemData.props = containerItem;
        containerItemData.id = containerItem.id;
        containerItemData.name = containerItemName;
        containerItemData.columnClassNames = containerItemColumnClassNames;
        validateComponent(
          containerItemData,
          containerItemType,
          flatComponentsArray,
        );
      }
    }
  }
}

/// Validate the component props
function validateComponent(
  componentData: any,
  componentType: string,
  flatComponentsArray: any[],
) {
  //TODO: Insert component based async external fetch logic here
  //! This is where the props are processed
  let vr: ValidationResult = {
    result: ValidationOutcomes.UNKNOWN,
    messages: [],
  };
  vr = validateComponentProps(componentData.props, componentType);

  if (vr.result === ValidationOutcomes.FAILURE) {
    debugError(
      `${componentData.id} has failed validation and will not be rendered.`,
      vr.messages,
    );
  }

  if (vr.result === ValidationOutcomes.UNKNOWN) {
    debugError(
      `${componentData.id} has unknown validation status and will not be rendered.`,
      vr.messages,
    );
  }

  if (vr.result === ValidationOutcomes.FAILURE) {
    debugError(
      `${componentData.id} has failed validation and will not be rendered.`,
      vr.messages,
    );
  }

  if (
    vr.result === ValidationOutcomes.SUCCESS ||
    vr.result === ValidationOutcomes.WARNING
  ) {
    const componentInfo = {
      isContainer: false,
      name: componentData.name,
      componentType: componentType,
      componentProps: vr.componentProps,
      gridInfo: getColumnClassInfo(componentData.columnClassNames),
    };

    flatComponentsArray.push(componentInfo);
  }
}

export const processData = async (aemServerData: any) => {
  const flatComponentsArray: any[] = [];
  cleanHeadlessData(aemServerData);

  const root = aemServerData?.[':items']?.root;
  const rootItems = root?.[':items'] || {};
  const authoredRootItemsOrder = root?.[':itemsOrder'];
  const rootItemsOrder =
    Array.isArray(authoredRootItemsOrder) && authoredRootItemsOrder.length > 0
      ? authoredRootItemsOrder
      : Object.keys(rootItems);

  for (const rootItemName of rootItemsOrder) {
    const rootItem = rootItems[rootItemName];
    if (!rootItem || typeof rootItem !== 'object' || Array.isArray(rootItem)) {
      continue;
    }
    const rootItemResourceType = getResourceType(rootItem);
    const rootItemType = stripPath(rootItemResourceType);
    const rootItemColumnClassNames =
      root?.columnClassNames?.[rootItemName] ||
      'aem-GridColumn aem-GridColumn--default--12';

    if (isFormContainerResourceType(rootItemResourceType)) {
      processFormComponent(
        rootItemName,
        rootItem,
        rootItemColumnClassNames,
        flatComponentsArray,
      );
    } else if ('container' === rootItemType) {
      if (flatComponentsArray.length > 0)
        flatComponentsArray.push({
          isContainer: false,
          name: `spacer-${Date.now()}`,
          componentType: 'spacer',
          componentProps: null,
          gridInfo: { width: 12, offset: 0 },
        });
      await processContainerData(rootItemName, rootItem, flatComponentsArray);
    } else if ('experience-fragment' === rootItemType) {
      await processExperienceFragmentData(
        rootItem,
        flatComponentsArray,
        rootItemColumnClassNames,
      );
    }
  }

  if (
    flatComponentsArray.length > 0 &&
    flatComponentsArray[flatComponentsArray.length - 1].componentType !==
      'spacer'
  )
    // last spacer for the page
    flatComponentsArray.push({
      isContainer: false,
      name: `spacer-${Date.now()}`,
      componentType: 'spacer',
      componentProps: null,
      gridInfo: { width: 12, offset: 0 },
    });

  return [
    {
      isContainer: true,
      name: 'AEM Root Container',
      columnCount: 12,
      gridInfo: { width: 12, offset: 0 },
      components: flatComponentsArray,
    },
  ];
};
